import React from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeFieldLabel, ExpirationDateInput as CDSExpirationDateInput, FieldLabel } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { isValidExpirationDate } from '@cash-web/shared-util-validation'

import { FormInput } from '../'
import { FieldController } from '../FieldController/FieldController'

type ExpirationDateInputProps = {
  name: string
  isRequired?: boolean
}

export const expirationDateInputFormatter =
  (prevValue = '') =>
  (value = '') => {
    const isBackspace = value === prevValue.slice(0, -1)
    // prevent formatting early and allow backspace
    if (value === '' || value === '1' || value === '0' || isBackspace) return value
    // autoformat invalid input
    if (value === '1/' || value === '0/') return '01/'

    const valueWithoutSlash = value.replace(/\//g, '')
    let month = valueWithoutSlash.substring(0, 2)
    let year = valueWithoutSlash.substring(2, 6)

    // Handle pasting 4-digit years and just take the last 2 digits
    if (year.length === 4) {
      year = year.substring(2, 4)
    }

    if (month.length === 1 && Number(month) > 1) {
      month = `0${Number(month)}`
    } else if (month.length === 2) {
      if (Number(month) === 0) {
        month = '01'
      } else if (Number(month) > 12) {
        month = '12'
      }
    }

    return `${month}/${year}`
  }

const keyExceptions = ['/', 'Backspace', 'Tab']
const isInvalidKey = (key: string) => Number.isNaN(Number(key)) && !keyExceptions.includes(key)

export const ExpirationDateInput: React.FC<ExpirationDateInputProps> = ({ name, isRequired, ...rest }) => {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const { watch, setValue } = useFormContext()
  const value = watch(name) ?? ''

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeFieldLabel
        label={t('Expiration', { context: 'This is the form field label for the expiration date input.' })}
      >
        <FieldController
          FieldComponent={CDSExpirationDateInput}
          componentProps={{
            ...rest,
            placeholder: t('MM/YY', { context: 'This is the placeholder for expiration date input.' }),
          }}
          name={name}
          rules={{
            required: {
              value: isRequired || false,
              message: t('Enter your expiration date.', {
                context:
                  'This is an error message shown below the expiration date input when there is no value entered.',
              }),
            },
            validate: (value: string) => {
              const { isValid, reason } = isValidExpirationDate(value)
              if (reason === 'pastDate') {
                return t('Expiration date must be in the future.', {
                  context:
                    'This is an error message shown below the expiration date input when the date entered has already past.',
                })
              }
              if (reason === 'invalidDate') {
                return t('Expiration date is invalid.', {
                  context: 'This is an error message shown below the expiration date input when the date is not valid.',
                })
              }

              return isValid
            },
          }}
        />
      </ArcadeFieldLabel>
    )
  }

  return (
    <FieldLabel label={t('Expiration', { context: 'This is the form field label for the expiration date input.' })}>
      <FormInput
        name={name}
        value={value}
        placeholder={t('MM/YY', { context: 'This is the placeholder for expiration date input.' })}
        autoComplete="cc-exp"
        transform={expirationDateInputFormatter(value)}
        onPaste={e => {
          // clipboardData is not available in all browsers (??)
          const clipboardData = e?.clipboardData?.getData('text')

          if (clipboardData == null) {
            return
          }

          if (isValidExpirationDate(clipboardData)) {
            setValue(name, clipboardData)
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        onKeyDown={e => {
          // Bypass for operations like pasting
          if (e.getModifierState('Meta') || e.getModifierState('Control') || e.getModifierState('Alt')) {
            return
          }

          const isInvalidSlash = e.key === '/' && (value.includes('/') || !value?.length)

          // prevents invalid keystrokes
          if (isInvalidSlash || isInvalidKey(e.key)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(name, e.target.value)
        }}
        rules={{
          required: {
            value: isRequired || false,
            message: t('Enter your expiration date.', {
              context: 'This is an error message shown below the expiration date input when there is no value entered.',
            }),
          },
          validate: value => {
            const { isValid, reason } = isValidExpirationDate(value)
            if (reason === 'pastDate') {
              return t('Expiration date must be in the future.', {
                context:
                  'This is an error message shown below the expiration date input when the date entered has already past.',
              })
            }
            if (reason === 'invalidDate') {
              return t('Expiration date is invalid.', {
                context: 'This is an error message shown below the expiration date input when the date is not valid.',
              })
            }

            return isValid
          },
        }}
      />
    </FieldLabel>
  )
}
